// Static site content: FAQ + legal / informational pages.
// Block convention: a string renders as a paragraph; { list: [...] } renders as a bullet list.
import { SUPPORT_EMAIL, CONTACT_EMAIL, CAREERS_EMAIL, PRICE, BASE_BOOK_PRICE, BOOK_TIERS } from "@/lib/pricing";

export const FAQS = [
  {
    q: "What is KLYR Studio?",
    a: [
      "KLYR Studio is a creative intelligence platform that helps you craft complete, publish-ready books — children's stories, fiction, non-fiction, and multi-book series — with cinematic emotional depth and narrative continuity.",
      "You guide the vision; KLYR Studio handles the heavy lifting.",
    ],
  },
  {
    q: "Who owns the books I create?",
    a: [
      "You do. You retain full copyright and full commercial rights to every book you create with KLYR Studio. KLYR Studio does not claim ownership, royalties, or distribution rights.",
      "You are the author. KLYR Studio is your creative tool.",
    ],
  },
  {
    q: "Can I sell my books on Amazon KDP or other platforms?",
    a: [
      "Yes. You can publish and sell your books anywhere — Amazon KDP, Barnes & Noble Press, IngramSpark, Etsy, your own website, or any marketplace you choose.",
      "Every book credit includes the complete Amazon KDP publishing package to help you prepare your book, but you are free to publish wherever you want.",
    ],
  },
  {
    q: "Are the books AI-generated?",
    a: [
      "KLYR Studio uses advanced AI to assist with writing, continuity, structure, and creative development. You remain the creative director and final decision-maker. You can edit, rewrite, regenerate, or customize any part of your book.",
      "KLYR Studio is a tool — not a ghostwriter.",
    ],
  },
  {
    q: "How does narrative continuity work?",
    a: [
      "KLYR Studio maintains a rolling story memory across pages, chapters, and series. This ensures:",
      { list: [
        "No random characters appearing",
        "No sudden topic jumps",
        "No unexplained objects or events",
        "No broken sentences",
        "No incoherent transitions",
      ] },
      "Every page continues the story exactly where the previous one left off.",
    ],
  },
  {
    q: "What genres can I create?",
    a: [
      "You can create:",
      { list: [
        "Children's books",
        "Fiction (all genres)",
        "Non-fiction",
        "Multi-book series",
        "Guided journals",
        "Educational books",
        "Inspirational books",
        "Custom formats",
      ] },
      "More modes will be added over time.",
    ],
  },
  {
    q: "What is a Series?",
    a: [
      "A Series is a multi-book narrative with strict continuity across all volumes. KLYR Studio keeps:",
      { list: [
        "Characters consistent",
        "Plot threads aligned",
        "Tone and style unified",
        "Story arcs connected",
      ] },
      `Series Mode is a one-time $${PRICE.series_unlock} unlock per series.`,
    ],
  },
  {
    q: "Can I use my own illustrations?",
    a: ["Yes. You can upload your own artwork, mix AI + human illustrations, or use external images you have rights to."],
  },
  {
    q: "What formats can I export?",
    a: [
      "KLYR Studio supports export formats suitable for:",
      { list: ["Amazon KDP", "Print-on-demand", "Digital distribution", "Personal use"] },
      "Every finished book includes Digital PDF, Word, print-ready interior and cover PDFs, validated EPUB and the KDP bundle. There is no extra publishing fee. Supported formats expand over time.",
    ],
  },
  {
    q: "Is my data private?",
    a: [
      "Yes. Your books, drafts, and uploads are private to your account. KLYR Studio does not share your content, and does not use your books to train models.",
      "For full details, see the Privacy Policy.",
    ],
  },
  {
    q: "What happens if the AI makes a mistake?",
    a: [
      "You can:",
      { list: [
        "regenerate any page",
        "revise any section",
        "edit the text directly",
        "request continuity fixes",
        "rebuild chapters",
        "adjust tone, pacing, or style",
      ] },
      "You have full creative control.",
    ],
  },
  {
    q: "Can I collaborate with others?",
    a: ["You can share your exported files, drafts, or final books with collaborators. Team features may be added in the future."],
  },
  {
    q: "Can I create books in other languages?",
    a: ["Yes — KLYR Studio supports multiple languages. Quality may vary depending on the language, but support continues to expand."],
  },
  {
    q: "Do you offer refunds?",
    a: ["Refunds are handled according to our Refund Policy. If something breaks or fails to generate, we'll make it right."],
  },
  {
    q: "Do you offer customer support?",
    a: [`Yes — you can reach us anytime at ${SUPPORT_EMAIL}. We respond quickly and personally.`],
  },
  {
    q: "Is there any restricted content?",
    a: [
      "KLYR Studio does not allow:",
      { list: [
        "hateful content",
        "explicit adult content",
        "harmful or dangerous content",
        "illegal content",
        "violent or graphic material",
        "anything violating publishing platform rules",
      ] },
      "This keeps the platform safe and compliant.",
    ],
  },
  {
    q: `How much does a book cost?`,
    a: [
      `One book credit makes one complete book, including its full Amazon KDP publishing package. Prices go down when you buy more at once, and the price applies to every book in the order:`,
      { list: [
        "1–2 books: $45 each",
        "3–5 books: $40 each",
        "6–8 books: $39 each",
        "9–11 books: $38 each",
        "12–14 books: $37 each",
        "15–17 books: $36 each",
        "18+ books: $35 each (the lowest price)",
      ] },
      `There are no subscriptions and no extra publishing fees. Your first 3 previews are free.`,
    ],
  },
  {
    q: `What does one book credit include?`,
    a: [
      { list: [
        "Complete book generation",
        "Cover creation in Cover Studio",
        "Book editor access",
        "Print-ready Amazon KDP interior PDF",
        "Print-ready Amazon KDP cover PDF (with bleed and spine)",
        "A complete downloadable ZIP package",
        "A README with step-by-step publishing instructions",
        "Editable manuscript files (DOCX, EPUB)",
        "Re-downloading your files any time",
      ] },
    ],
  },
  {
    q: `How do credits work?`,
    a: [
      `Credits are one-time purchases that never expire. Each credit turns one idea (or one free preview) into a complete, publish-ready book. If a book fails to generate, its credit is returned to you automatically.`,
    ],
  },
  {
    q: `How many credits do I have left?`,
    a: [
      `Open Account (your name in the top-right menu). Your book-credit balance is shown at the top, along with any free previews you still have.`,
    ],
  },
  {
    q: `Where can I find my receipts?`,
    a: [
      `A receipt is emailed to you right after every purchase. You can also see every paid order, with its date and amount, under Account → Payment history & receipts.`,
    ],
  },
  {
    q: `Where are my finished books and downloads?`,
    a: [
      `Every book you create is saved in My Library. Open a book and use Downloads to get the KDP ZIP package, PDFs, Word and EPUB files again, as often as you like.`,
    ],
  },
  {
    q: `How long does it take to make a book?`,
    a: [
      `It depends on the book's length and type, and on how many books are ahead of yours. While you wait you'll see your real place in line and which stage your book is in (planning, writing chapter X of Y, cover and final checks). We don't show made-up percentages or time estimates.`,
    ],
  },
  {
    q: `Can I close my browser while my book is being made?`,
    a: [
      `Yes. Your book is made on our servers and saved as it goes. If our servers restart, it carries on from its last finished chapter and keeps its place in line. You'll find it in My Library, and we email you when it's ready.`,
    ],
  },
  {
    q: `What happens if I click Create twice?`,
    a: [
      `You only get one book and pay for one credit. Each request is recognised, so a double-click or a dropped connection never charges you twice. Starting a genuinely new book with the same settings still works.`,
    ],
  },
  {
    q: `How do I publish my book on Amazon KDP?`,
    a: [
      `Download your KDP ZIP package and open KLYR_README.pdf first: it lists your trim size, paper type, page count and spine width. Then in KDP:`,
      { list: [
        "Create → Paperback: enter the title, author and the Amazon description from your README",
        "Manuscript: upload the interior PDF from the ZIP",
        "Book Cover: choose “Upload a cover you already have” and upload the cover PDF from the ZIP",
        "Print options: choose the same trim size as your file names and WHITE paper",
        "Use KDP's Previewer to check every page, then publish",
      ] },
      `For a Kindle eBook, choose Create → Kindle eBook and upload the EPUB from the same ZIP. See our Publishing Guide for screenshots.`,
    ],
  },
  {
    q: `Which book size should I choose?`,
    a: [
      `6 × 9 in suits most novels and non-fiction. 8.5 × 11 in suits workbooks, puzzle, coloring and large-print books. 8.5 × 8.5 in suits picture books. Always choose the same size in KDP that you made the book in.`,
    ],
  },
  {
    q: `What is a “Not print-ready” cover?`,
    a: [
      `In Cover Studio, a red “Not print-ready” label marks a saved layout that Amazon's paperback check would reject (for example, text too close to the edge). You can still use it for ebooks; choose a different layout for the paperback.`,
    ],
  },
  {
    q: `I forgot my password. What do I do?`,
    a: [
      `On the sign-in screen, tap “Forgot password?” and enter your email. We'll send a link that works once and expires after 60 minutes. Changing your password signs you out on other devices.`,
    ],
  },
  {
    q: `What happened to Unlimited?`,
    a: [
      `Unlimited is no longer offered to new customers. If you already have an active Unlimited plan, nothing changes for you: it keeps working until it ends, and you keep every book you've made. Credits you bought earlier are also kept and still make 1 book each.`,
    ],
  },
  {
    q: `Is KLYR Studio still being improved?`,
    a: [
      `Yes, every day. New features and improvements are released constantly, and some features are still in development, so you may notice things changing or getting better between visits. If anything doesn't work the way you expect, tell us at support@klyrstudio.io; your feedback shapes what we build next.`,
    ],
  },
];

export const PAGES = {
  guide: {
    title: "Publishing Guide",
    intro: "From idea to a live Amazon listing, step by step. KDP screenshots with labels are being added to this guide.",
    sections: [
      { h: "1. Create your book", body: [
        "Sign in, tap Create, describe your idea and choose the book type and trim size. Your first 3 previews are free; one book credit unlocks the complete book.",
        "After you submit, you'll see your real place in line and which stage your book is in. You can close the page: your book keeps being made and is saved in My Library.",
      ] },
      { h: "2. Review, edit and choose your cover", body: [
        "Open the finished book to read and edit it. Use Cover Studio to compare cover layouts side by side (including at Amazon thumbnail size) before choosing one. Every cover you've had is saved.",
        "Avoid layouts marked “Not print-ready” for your paperback.",
      ] },
      { h: "3. Download your KDP package", body: [
        "Open Downloads and get the KDP ZIP package. It contains:",
        { list: ["KLYR_README.pdf: START HERE. Your trim size, paper type, page count, spine width and Amazon description",
                 "The interior PDF (your manuscript)", "The full paperback cover PDF (front, spine and back, with bleed)",
                 "An EPUB for the Kindle eBook", "A spec sheet and the print-check report"] },
      ] },
      { h: "4. Paperback on Amazon KDP: where to paste what", body: [
        { list: ["KDP Bookshelf → Create → Paperback",
                 "Paperback Details: Title, Subtitle and Author exactly as in your README; paste the Amazon description from the README into Description",
                 "Keywords: copy up to 7 keyword ideas from your book's Marketing kit",
                 "Paperback Content → Print ISBN: choose KDP's free ISBN (or your own)",
                 "Print Options: Ink = Black (or as noted in the README), Paper = WHITE, Trim size = the size in your file names, Bleed = as listed in the README",
                 "Manuscript: Upload paperback manuscript → choose the interior PDF from the ZIP",
                 "Book Cover: choose “Upload a cover you already have” → upload the cover PDF from the ZIP",
                 "Launch Previewer, check every page, then Approve → set your price on Paperback Rights & Pricing → Publish"] },
      ] },
      { h: "5. Kindle eBook", body: [
        { list: ["KDP Bookshelf → Create → Kindle eBook", "Use the same title, author, description and keywords",
                 "Manuscript: upload the EPUB from the ZIP", "Kindle eBook Cover: upload the front cover image from your book's Downloads",
                 "Preview, set your price and publish. Amazon usually reviews new titles within 72 hours."] },
      ] },
      { h: "Need help?", body: ["Read the FAQ or email support@klyrstudio.io. KLYR Studio is improved every day, and some features are still in development."] },
    ],
  },
  about: {
    title: "About KLYR Studio",
    intro: "A creative intelligence studio for anyone with a story to tell.",
    sections: [
      {
        h: "Our mission",
        body: [
          "KLYR Studio exists to remove the friction between an idea and a finished, publish-ready book. Whether you're writing a children's picture book, a full novel, a non-fiction guide, or an entire multi-book series, KLYR gives you a studio that handles research, structure, illustration, narrative continuity and export — while you stay firmly in the creative director's seat.",
        ],
      },
      {
        h: "How it works",
        body: [
          "KLYR researches and drafts across multiple leading AI models in parallel, then synthesizes the best of each into one cohesive manuscript. A rolling story memory keeps every page, chapter and series volume consistent, so your characters, world and plot never drift.",
          "You can edit, reword, regenerate and rearrange anything, add your own illustrations, generate covers and audiobooks, and export a page-perfect PDF, Word document, EPUB or an Amazon KDP-ready bundle.",
        ],
      },
      {
        h: "You own your work",
        body: [
          "Every book you create is 100% yours — full copyright, full commercial rights, and every royalty. KLYR Studio is your tool, not your co-author.",
        ],
      },
      {
        h: "Get in touch",
        body: [`Questions, ideas or feedback? Reach us at ${CONTACT_EMAIL}. We'd love to hear what you're building.`],
      },
    ],
  },

  terms: {
    title: "Terms of Service",
    intro: "The agreement between you and KLYR Studio.",
    sections: [
      { h: "1. Acceptance of terms", body: ["By creating an account or using KLYR Studio, you agree to these Terms of Service. If you do not agree, please do not use the service."] },
      { h: "2. Your account", body: ["You are responsible for keeping your login credentials secure and for all activity under your account. You must provide accurate information and be old enough to form a binding contract in your jurisdiction."] },
      { h: "3. Ownership of your books", body: ["You retain full copyright and full commercial rights to the books you create. KLYR Studio claims no ownership, royalties, or distribution rights over your finished work. You are free to publish and sell your books on any platform you choose."] },
      { h: "4. Acceptable use", body: [
        "You agree not to use KLYR Studio to create or distribute:",
        { list: ["hateful content", "explicit adult content", "harmful or dangerous content", "illegal content", "violent or graphic material", "anything that violates the rules of publishing platforms"] },
        "We may suspend or terminate accounts that violate this policy.",
      ] },
      { h: "5. AI-assisted content", body: ["KLYR Studio uses AI to assist with writing, structure and illustration. AI output can contain errors or inaccuracies. You are the creative director and are responsible for reviewing, editing and verifying your book before publishing it."] },
      { h: "6. Plans, credits & add-ons", body: [
        `Books are bought as one-time credits: $${BASE_BOOK_PRICE} per book, with volume pricing down to $${BOOK_TIERS[BOOK_TIERS.length - 1].unit_price} per book for ${BOOK_TIERS[BOOK_TIERS.length - 1].min}+ books in one order. Each credit makes one complete book including its Amazon KDP publishing package. Credits never expire. Series Mode ($${PRICE.series_unlock}/series) is an optional add-on.`,
        "Unlimited plans are no longer sold. Existing Unlimited subscribers keep their plan until it ends or is cancelled.",
      ] },
      { h: "7. Refunds", body: ["Refunds are handled according to our Refund Policy. If a book fails to generate or something breaks, contact us and we'll make it right."] },
      { h: "8. Disclaimers", body: ["The service is provided \u201cas is\u201d without warranties of any kind. We do not guarantee that AI output will be error-free, uninterrupted, or fit for a particular purpose."] },
      { h: "9. Limitation of liability", body: ["To the maximum extent permitted by law, KLYR Studio is not liable for indirect, incidental or consequential damages arising from your use of the service."] },
      { h: "10. Changes to these terms", body: ["We may update these terms from time to time. Continued use of the service after changes take effect constitutes acceptance of the updated terms."] },
      { h: "11. Contact", body: [`Questions about these terms? Email ${SUPPORT_EMAIL}.`] },
    ],
  },

  privacy: {
    title: "Privacy Policy",
    intro: "How we handle your data and your creative work.",
    sections: [
      { h: "Information we collect", body: [
        "We collect the information you provide to run your account and service, including:",
        { list: ["your name and email address", "the books, drafts and uploads you create", "billing details processed securely by our payment provider"] },
      ] },
      { h: "How we use your information", body: ["We use your information to operate your account, generate and store your books, process payments, provide support, and improve the reliability of the service."] },
      { h: "Your content is private", body: ["Your books, drafts, and uploads are private to your account. We do not share your content with other users, and we do not use your books to train AI models."] },
      { h: "Payments", body: ["Payments are processed by Stripe. We do not store your full card details on our servers."] },
      { h: "Cookies & sessions", body: ["We use a secure session cookie to keep you signed in. We do not sell your data to third parties."] },
      { h: "Data retention & deletion", body: [`You can delete your books at any time. To request full account deletion, contact ${SUPPORT_EMAIL} and we will remove your personal data, subject to any legal retention obligations.`] },
      { h: "Your rights", body: ["Depending on where you live, you may have the right to access, correct, or delete your personal data. Contact us to exercise these rights."] },
      { h: "Contact", body: [`Privacy questions? Email ${SUPPORT_EMAIL}.`] },
    ],
  },

  refund: {
    title: "Refund Policy",
    intro: "If something breaks or fails to generate, we'll make it right.",
    sections: [
      { h: "Failed generations", body: ["If a book fails to generate, the generation credit is automatically returned to your account so you can try again at no additional cost."] },
      { h: "Existing Unlimited subscriptions", body: ["Unlimited is no longer sold, but if you already have it you can cancel anytime and keep access until the end of your current billing period. Subscription fees already billed are generally non-refundable, but if you were charged in error, contact us and we'll fix it."] },
      { h: "Book credits & add-ons", body: ["Book credits (each includes the book's Amazon KDP publishing package) and Series Mode unlocks are one-time purchases. Because they unlock digital deliverables, they are generally non-refundable once used. If a purchase didn't deliver what it promised, we'll make it right."] },
      { h: "How to request a refund", body: [`Email ${SUPPORT_EMAIL} with your account email and a short description of the issue. We review every request personally and respond quickly.`] },
    ],
  },

  careers: {
    title: "Careers",
    intro: "Help us build the future of book creation.",
    widget: "careers",
    sections: [
      { h: "Who we are", body: ["KLYR Studio is a small, product-obsessed team building creative tools that let anyone turn an idea into a finished, publishable book."] },
      { h: "How we work", body: ["We ship fast, care deeply about craft, and treat our users like the authors they are. We value curiosity, ownership and clear writing over pedigree. Everyone works remotely, on flexible hours."] },
    ],
  },

  support: {
    title: "Support",
    intro: "Real people, quick answers. We usually reply within one business day.",
    widget: "support",
    sections: [
      { h: "Email us", body: [`Write to ${SUPPORT_EMAIL} any time. Include your account email and, if it's about a book, the book's title.`] },
      { h: "Before you write", body: [{ list: [
        { pre: "Most questions about credits, previews and pricing are answered in our ", page: "faq", label: "FAQ", post: "." },
        { pre: "Our ", page: "guide", label: "Publishing Guide", post: " walks you through uploading your files to Amazon KDP." },
        { pre: "Refund questions are covered in our ", page: "refund", label: "Refund Policy", post: "." },
      ] }] },
      { h: "Open a support ticket", body: ["Use the form below and you'll get a ticket number by email straight away. Please don't include passwords or card numbers."] },
    ],
  },

  contact: {
    title: "Contact us",
    intro: "We respond quickly and personally.",
    sections: [
      { h: "Support", body: [`For help with your account, billing, or a book, email ${SUPPORT_EMAIL}.`] },
      { h: "General enquiries", body: [`For partnerships, press or anything else, email ${CONTACT_EMAIL}.`] },
      { h: "Careers", body: [`Interested in joining the team? Email ${CAREERS_EMAIL}.`] },
    ],
  },
};

export const INFO_MENU = [
  { key: "faq", label: "FAQ" },
  { key: "guide", label: "Publishing Guide" },
  { key: "about", label: "About" },
  { key: "terms", label: "Terms of Service" },
  { key: "privacy", label: "Privacy Policy" },
  { key: "refund", label: "Refund Policy" },
  { key: "careers", label: "Careers" },
  { key: "support", label: "Support" },
  { key: "contact", label: "Contact us" },
];
